import React, { FC, useState } from 'react';

import { ClipboardButton, Select, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { PageLayout } from 'client/blocks/Layout/PageLayout';
import { Skill } from 'client/model/skill';
import { generateDeepLink, generateLink } from 'client/utils/promotion';
import { Badge, badges } from 'client/utils/promotion-badges';
import * as commonKeyset from 'i18n/Common';
import * as keyset from 'i18n/Promotion';
import * as settingsKeyset from 'i18n/Settings';

import styles from './AliceAppPromotionPage.module.scss';

interface Props {
    skill: Skill;
}

const t = i18n(keyset);
const tSettings = i18n(settingsKeyset);
const tCommon = i18n(commonKeyset);

export const getThemeOptions = () =>
    [
        { value: 'black', content: t('color-black') },
        { value: 'white', content: t('color-white') },
        { value: 'black-monochrome', content: t('color-black-transparent') },
        { value: 'white-monochrome', content: t('color-white-transparent') },
    ] as const;

export const AliceAppPromotionPage: FC<Props> = ({ skill }) => {
    const [theme, setTheme] = useState(Badge.Black);

    const { Badge: BadgeComp, term } = badges[theme];

    const skillVisibleInStore = skill.onAir && !skill.hideInStore;

    if (!skillVisibleInStore) {
        return (
            <PageLayout aliceApp={skill} pageTitle={tSettings('menu-promo')}>
                <h4 className={styles.aliceAppPromotionPage__blockHeader}>{t('header-badge-for-site')}</h4>
                <div>{t('error-no-available')}</div>
                <h4 className={styles.aliceAppPromotionPage__blockHeader}>{t('header-direct-link')}</h4>
                <div>{t('error-no-available')}</div>
            </PageLayout>
        );
    }

    const link = generateLink({ slug: skill.slug, term });
    const deepLink = generateDeepLink(skill.id);

    const themeOptions = getThemeOptions();

    return (
        <ErrorBoundary withHomepageLink>
            <PageLayout aliceApp={skill} pageTitle={tSettings('menu-promo')}>
                <div className={styles.aliceAppPromotionPage__badgeContainer}>
                    <BadgeComp />
                </div>
                <h4 className={styles.aliceAppPromotionPage__blockHeader}>{t('header-badge-for-site')}</h4>
                <Select
                    onUpdate={(value) => {
                        setTheme(value?.[0] as Badge);
                    }}
                    value={[theme]}
                    width="max"
                    size="xl"
                >
                    {themeOptions.map(({ value, content }) => (
                        <Select.Option key={content} value={value}>
                            {content}
                        </Select.Option>
                    ))}
                </Select>

                <h4 className={styles.aliceAppPromotionPage__blockHeader}>{t('label-text-copy')}</h4>
                <div className={styles.aliceAppPromotionPage__linkContainer}>
                    <div className={styles.aliceAppPromotionPage__link}>{link}</div>
                    <ClipboardButton
                        text={link}
                        title={t('label-text-copy')}
                        tooltipInitialText={tCommon('to-copy')}
                        tooltipSuccessText={tCommon('copied')}
                        size="l"
                        view="flat-secondary"
                    />
                </div>
                <h4 className={styles.aliceAppPromotionPage__blockHeader}>{t('header-direct-link')}</h4>
                <TextInput
                    value={deepLink}
                    size="xl"
                    endContent={
                        <ClipboardButton
                            text={deepLink}
                            tooltipInitialText={tCommon('to-copy')}
                            tooltipSuccessText={tCommon('copied')}
                            size="l"
                            view="flat-secondary"
                        />
                    }
                    readOnly
                />
            </PageLayout>
        </ErrorBoundary>
    );
};
